import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AlertTriangle, Clock, FileX, CreditCard, LogOut, RefreshCw, Upload, CheckCircle, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import BilanBlockForm from "./BilanBlockForm";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const BLOCK_ICONS = {
  validation: Clock,
  documents: FileX,
  paiement: CreditCard,
};

const BLOCK_COLORS = {
  validation: "text-amber-600 bg-amber-100",
  documents: "text-red-600 bg-red-100",
  paiement: "text-orange-600 bg-orange-100",
};

const DOCUMENT_LABELS = {
  "carte_identite": "Carte d'identité",
  "assurance_rc_pro": "Assurance RC Pro",
  "carte_commercant": "Carte de commerçant ambulant",
  "assurance_vehicule": "Assurance véhicule",
  "kbis": "Extrait KBIS",
  "haccp": "Certificat HACCP"
};

export default function BlockedPage() {
  const [blockInfo, setBlockInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [documentUpdates, setDocumentUpdates] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check if returning from Stripe payment
    const paymentStatus = searchParams.get("payment");
    if (paymentStatus === "success") {
      toast.success("Paiement réussi ! Votre compte est en cours de déblocage...");
    } else if (paymentStatus === "cancelled") {
      toast.error("Paiement annulé");
    }
    
    checkAccessStatus();
  }, []);

  const checkAccessStatus = async () => {
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/auth/access-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.data.is_blocked) {
        toast.success("Votre compte est maintenant actif !");
        navigate("/dashboard");
      } else {
        setBlockInfo(response.data);
        
        // Initialize document updates state for expired docs
        if (response.data.expired_documents) {
          const initialState = {};
          response.data.expired_documents.forEach(doc => {
            initialState[doc.type] = {
              date_expiration: "",
              file_id: null
            };
          });
          setDocumentUpdates(initialState);
        }
      }
    } catch (error) {
      console.error("Error checking access status:", error);
      if (error.response?.status === 401) {
        logout();
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handlePayDebt = async () => {
    setPaymentLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/pay-debt`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Error creating payment:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création du paiement");
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleFileUpload = async (docType, file) => {
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      setUploadedFiles(prev => ({
        ...prev,
        [docType]: { name: file.name, id: response.data.file_id }
      }));
      
      setDocumentUpdates(prev => ({
        ...prev,
        [docType]: {
          ...prev[docType],
          file_id: response.data.file_id
        }
      }));
      
      toast.success(`${DOCUMENT_LABELS[docType] || docType} téléchargé`);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleDateChange = (docType, date) => {
    setDocumentUpdates(prev => ({
      ...prev,
      [docType]: {
        ...prev[docType],
        date_expiration: date
      }
    }));
  };

  const handleSubmitDocuments = async () => {
    // Validate all documents have file and date
    const documents = [];
    let hasError = false;
    
    for (const doc of blockInfo.expired_documents || []) {
      const update = documentUpdates[doc.type];
      if (!update?.file_id || !update?.date_expiration) {
        toast.error(`Veuillez compléter le document: ${DOCUMENT_LABELS[doc.type] || doc.type}`);
        hasError = true;
        break;
      }
      documents.push({
        document_type: doc.type,
        file_id: update.file_id,
        date_expiration: update.date_expiration
      });
    }
    
    if (hasError) return;
    
    setUploadingDocs(true);
    try {
      const response = await axios.post(`${API_URL}/auth/update-expired-documents`, documents, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      checkAccessStatus();
    } catch (error) {
      console.error("Error updating documents:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setUploadingDocs(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F9F7F5] via-[#F0EBE6] to-[#F9F7F5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!blockInfo) {
    return null;
  }

  const BlockIcon = BLOCK_ICONS[blockInfo.block_type] || AlertTriangle;
  const colorClass = BLOCK_COLORS[blockInfo.block_type] || "text-gray-600 bg-gray-100";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F9F7F5] via-[#F0EBE6] to-[#F9F7F5] flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-red-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-lg relative z-10">
        <Card className="bg-white/90 backdrop-blur border-[#E5E0DC] shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mx-auto mb-4 ${colorClass}`}>
              <BlockIcon className="w-10 h-10" />
            </div>
            <CardTitle className="text-2xl font-bold text-secondary">
              Accès temporairement restreint
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {blockInfo.reason}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {blockInfo.details && blockInfo.details.length > 0 && (
              <div className="bg-[#F9F7F5] rounded-lg p-4 space-y-2">
                <h3 className="font-semibold text-secondary mb-2">Détails :</h3>
                <ul className="space-y-2">
                  {blockInfo.details.map((detail, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Validation pending */}
            {blockInfo.block_type === "validation" && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-medium text-amber-800 mb-2">En attente de validation</h4>
                <p className="text-sm text-amber-700">
                  Un administrateur va vérifier vos documents et valider votre inscription. 
                  Vous recevrez une notification une fois votre compte activé.
                </p>
              </div>
            )}

            {/* Expired documents - with upload form */}
            {blockInfo.block_type === "bilan" && <BilanBlockForm />}
            {blockInfo.block_type === "documents" && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-800 mb-2">Documents expirés</h4>
                  <p className="text-sm text-red-700">
                    Veuillez mettre à jour vos documents expirés ci-dessous pour retrouver l'accès.
                  </p>
                </div>
                
                {/* Document upload forms */}
                <div className="space-y-4 bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-secondary flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Mettre à jour les documents
                  </h4>
                  
                  {(blockInfo.expired_documents || []).map((doc) => (
                    <div key={doc.type} className="p-3 bg-gray-50 rounded-lg space-y-3">
                      <Label className="font-medium text-sm">
                        {DOCUMENT_LABELS[doc.type] || doc.type}
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      
                      <div className="flex gap-2">
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileUpload(doc.type, e.target.files[0])}
                          className="flex-1"
                        />
                        {uploadedFiles[doc.type] && (
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-2" />
                        )}
                      </div>
                      
                      <div>
                        <Label className="text-xs text-gray-500">Nouvelle date d'expiration</Label>
                        <Input
                          type="date"
                          value={documentUpdates[doc.type]?.date_expiration || ""}
                          onChange={(e) => handleDateChange(doc.type, e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  ))}
                  
                  <Button
                    onClick={handleSubmitDocuments}
                    disabled={uploadingDocs}
                    className="w-full bg-primary hover:bg-primary/90"
                    data-testid="submit-documents-btn"
                  >
                    {uploadingDocs ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Soumettre les documents
                  </Button>
                  
                  <p className="text-xs text-center text-gray-500">
                    Après soumission, le bureau validera vos nouveaux documents.
                  </p>
                </div>
              </div>
            )}

            {/* Payment block - with pay button */}
            {blockInfo.block_type === "paiement" && (
              <div className="space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-medium text-orange-800 mb-2">Paiement en attente</h4>
                  <p className="text-sm text-orange-700">
                    Votre compte a été bloqué après 3 relances pour impayés. 
                    Régularisez votre situation pour retrouver l'accès.
                  </p>
                </div>
                
                {/* Payment amount and button */}
                <div className="bg-white border-2 border-orange-300 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 mb-2">Montant à régler</p>
                  <p className="text-3xl font-bold text-orange-600 mb-4">
                    {blockInfo.solde_du?.toFixed(2) || "0.00"} €
                  </p>
                  
                  <Button
                    onClick={handlePayDebt}
                    disabled={paymentLoading}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                    size="lg"
                    data-testid="pay-debt-btn"
                  >
                    {paymentLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    Payer maintenant
                  </Button>
                  
                  <p className="text-xs text-gray-500 mt-3 flex items-center justify-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                    Paiement sécurisé par Stripe
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                variant="outline"
                onClick={checkAccessStatus}
                className="flex-1 border-[#E5E0DC]"
                data-testid="refresh-status-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
              <Button
                variant="destructive"
                onClick={handleLogout}
                className="flex-1"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Se déconnecter
              </Button>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              Besoin d'aide ? Contactez-nous à{" "}
              <a href="mailto:contact@foodtruckunited.fr" className="text-primary hover:underline">
                contact@foodtruckunited.fr
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
